import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

import { t } from '../lib/i18n';
import type { ImagePoint, ManualQuad } from '../lib/types';
import { useAppStore } from '../store';

interface CropModalProps {
  /** The session's original uploaded file. Decoded once into an object URL. */
  file: File;
  /** Filename for the modal heading; falls back to the file's own name. */
  filename?: string;
  /** User clicked Apply with the four corners (in original-image pixels). */
  onApply: (corners: ManualQuad) => void;
  /** User clicked Cancel / pressed Escape / clicked the backdrop. */
  onClose: () => void;
}

/**
 * Manual-crop fallback. Used when the auto-detector lands on the wrong
 * quad — common on backgrounds where the receipt's edge isn't the
 * dominant gradient (textured wood, near-paper-coloured cement, scanner
 * vignettes). The user drags four corner handles around the actual
 * paper boundary; we send the resulting quad to the worker for a
 * direct-warp reprocess.
 *
 * Coordinate spaces:
 *   - "natural"  — the source image's intrinsic pixel grid, what the
 *                  worker wants to receive
 *   - "display"  — CSS pixels of the rendered <img>, where the user
 *                  actually clicks. Scaled uniformly from natural by
 *                  the fit-to-viewport sizing below.
 *
 * Corners are stored in display-space while editing (no rounding error
 * accumulation across drags) and converted to natural coords at submit
 * time. Initial positions are the image's four corners — a naive
 * starting layout that's always inside the image bounds and obvious to
 * adjust.
 *
 * Pointer events (not mouse + touch separately) so a single drag handler
 * works on desktop, iOS, and Android. The modal is otherwise a fixed
 * overlay rather than a <dialog> — <dialog> styling is awkward on iOS
 * Safari and we don't need its built-in focus trap for a single
 * primary action.
 */

const HANDLE_RADIUS_PX = 16;
const CORNER_LABELS = ['1', '2', '3', '4'] as const;

export function CropModal({ file, filename, onApply, onClose }: CropModalProps) {
  const lang = useAppStore((s) => s.lang);
  // Object URL for the rendered <img>. Created once when the modal
  // mounts, revoked on unmount. The component re-renders on corner
  // drags; recreating the URL on each render would leak.
  const objectUrl = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => {
    return () => URL.revokeObjectURL(objectUrl);
  }, [objectUrl]);

  // Natural-space dimensions, populated from the <img>'s onLoad. Until
  // then the corner handles can't render — we don't know how big the
  // image actually is. Using the rendered <img>'s naturalWidth/Height
  // covers HEIC, PDF page rendering (which becomes a JPEG before this
  // modal sees it) and any other format the browser can decode.
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  // Container size measured via ResizeObserver. The image's display
  // size (display-space) is computed from this so the four handles
  // stay aligned with the visually-rendered image as the viewport
  // resizes (e.g., orientation change on phones).
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (el === null) return;
    const update = (): void => {
      const r = el.getBoundingClientRect();
      setContainerSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Display dimensions: scale natural to fit inside the container while
  // preserving aspect ratio.
  const display = useMemo(() => {
    if (natural === null || containerSize === null) return null;
    const scale = Math.min(
      containerSize.w / natural.w,
      containerSize.h / natural.h,
    );
    return {
      w: natural.w * scale,
      h: natural.h * scale,
      scale,
      // Centre the display rect within the container.
      offsetX: (containerSize.w - natural.w * scale) / 2,
      offsetY: (containerSize.h - natural.h * scale) / 2,
    };
  }, [natural, containerSize]);

  // Corners in display-space coords (offsets relative to the display
  // image's top-left, NOT the container's). Initialised to the image's
  // own four corners once display dims are known.
  const [corners, setCorners] = useState<[ImagePoint, ImagePoint, ImagePoint, ImagePoint] | null>(
    null,
  );
  useEffect(() => {
    if (display === null || corners !== null) return;
    setCorners([
      { x: 0, y: 0 },
      { x: display.w, y: 0 },
      { x: display.w, y: display.h },
      { x: 0, y: display.h },
    ]);
  }, [display, corners]);

  // Active drag state. cornerIdx is which handle the user grabbed;
  // pointerId tracks the specific pointer so a second touch doesn't
  // hijack the drag. Cleared on pointerup / pointercancel.
  const dragRef = useRef<{ cornerIdx: number; pointerId: number } | null>(null);

  const handlePointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    cornerIdx: number,
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { cornerIdx, pointerId: e.pointerId };
    // Capture so subsequent move events come to this element even if
    // the cursor leaves the handle's hit area mid-drag.
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== e.pointerId) return;
    if (display === null || corners === null) return;
    const containerEl = containerRef.current;
    if (containerEl === null) return;
    const rect = containerEl.getBoundingClientRect();
    // Convert client coords → container coords → display-rect coords
    // (subtracting the centring offset). Clamp to the display rect so
    // a corner can never sit outside the visible image.
    const xInContainer = e.clientX - rect.left;
    const yInContainer = e.clientY - rect.top;
    const x = clamp(xInContainer - display.offsetX, 0, display.w);
    const y = clamp(yInContainer - display.offsetY, 0, display.h);
    setCorners((prev) => {
      if (prev === null) return prev;
      const next = [...prev] as [ImagePoint, ImagePoint, ImagePoint, ImagePoint];
      next[drag.cornerIdx] = { x, y };
      return next;
    });
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
    }
  };

  // Escape closes. Click on the backdrop (not the modal panel) closes too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const apply = (): void => {
    if (corners === null || display === null) return;
    // Convert display-space corners back to natural-space pixel coords.
    const inv = 1 / display.scale;
    const result: ManualQuad = [
      { x: corners[0].x * inv, y: corners[0].y * inv },
      { x: corners[1].x * inv, y: corners[1].y * inv },
      { x: corners[2].x * inv, y: corners[2].y * inv },
      { x: corners[3].x * inv, y: corners[3].y * inv },
    ];
    onApply(result);
  };

  // Polygon path for the visual overlay connecting the four corners.
  // SVG renders in display-space; offsetX/Y push it into the centred
  // image rect.
  const polygonPoints =
    corners === null
      ? ''
      : corners.map((c) => `${c.x},${c.y}`).join(' ');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t(lang, 'crop.title')}
      className="fixed inset-0 z-50 flex flex-col bg-slate-950/80 backdrop-blur-sm dark:bg-black/80"
      onClick={(e) => {
        // Backdrop click — only close when the click is on the overlay
        // itself, not on a child of the modal panel below.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-slate-700 bg-slate-900 px-4 py-3 text-slate-100">
        <div>
          <h2 className="text-base font-semibold">{t(lang, 'crop.title')}</h2>
          <p className="text-xs text-slate-400">
            {t(lang, 'crop.description')}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t(lang, 'crop.close')}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div
        ref={containerRef}
        className="relative flex-1 select-none overflow-hidden touch-none"
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <img
          src={objectUrl}
          alt={filename ?? file.name}
          className="pointer-events-none absolute inset-0 m-auto select-none"
          style={
            display === null
              ? undefined
              : { width: display.w, height: display.h }
          }
          onLoad={(e) => {
            const img = e.currentTarget;
            setNatural({ w: img.naturalWidth, h: img.naturalHeight });
          }}
          draggable={false}
        />

        {display !== null && corners !== null && (
          <>
            <svg
              className="pointer-events-none absolute"
              style={{
                left: display.offsetX,
                top: display.offsetY,
                width: display.w,
                height: display.h,
              }}
              viewBox={`0 0 ${display.w} ${display.h}`}
            >
              <polygon
                points={polygonPoints}
                fill="rgba(99, 102, 241, 0.15)"
                stroke="rgb(129, 140, 248)"
                strokeWidth={2}
              />
            </svg>
            {corners.map((c, i) => (
              <div
                key={i}
                onPointerDown={(e) => handlePointerDown(e, i)}
                role="button"
                tabIndex={0}
                aria-label={t(lang, 'crop.cornerAria', { n: i + 1 })}
                style={{
                  left: display.offsetX + c.x - HANDLE_RADIUS_PX,
                  top: display.offsetY + c.y - HANDLE_RADIUS_PX,
                  width: HANDLE_RADIUS_PX * 2,
                  height: HANDLE_RADIUS_PX * 2,
                }}
                className="absolute flex cursor-grab items-center justify-center rounded-full border-2 border-indigo-300 bg-indigo-500/70 text-xs font-bold text-white shadow-md ring-2 ring-white/20 transition-transform active:scale-110 active:cursor-grabbing"
              >
                {CORNER_LABELS[i]}
              </div>
            ))}
          </>
        )}
      </div>

      <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-slate-700 bg-slate-900 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-800 hover:text-slate-100"
        >
          {t(lang, 'crop.cancel')}
        </button>
        <button
          type="button"
          onClick={apply}
          disabled={corners === null}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
        >
          {t(lang, 'crop.apply')}
        </button>
      </div>
    </div>
  );
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}
